import {
	type DuplicateReviewStatus,
	duplicateReviewSchema,
	type ImageReviewStatus,
	imageReviewSchema,
	type LocationSource,
	type LocationVerification,
	locationImageSchema,
	locationReportSchema,
	locationReportSummarySchema,
	locationSchema,
	type SatelliteScreenResolution,
	satelliteScreenSchema,
	scrapeBulkResponseSchema,
	scrapeRunResultSchema,
	screenRunResponseSchema,
	type UpdateDuplicateReview,
	type UpdateImageReview,
	type UpdateLocationImage,
	type UpdateSatelliteScreen,
} from "../schemas/location";
import type { ReportStatus, UpdateReportStatus } from "../schemas/report";
import type { SortOrder } from "./games";
import { fetchApi, fetchFormData, fetchVoid, paginatedSchema, toSearchParams } from "./internal";

const LOCATION_IMAGE_FIELD = "image";

export interface LocationsListParams {
	limit?: number;
	offset?: number;
	q?: string;
	sort_by?: string;
	order?: SortOrder;
	source?: LocationSource;
	verification?: LocationVerification;
	is_hidden?: boolean;
	country?: string;
	city?: string;
	needs_images?: boolean;
}

export const locations = {
	create: (data: unknown) =>
		fetchApi("/admin/locations", locationSchema, {
			body: JSON.stringify(data),
			method: "POST",
		}),
	delete: (id: string) => fetchVoid(`/admin/locations/${id}`, { method: "DELETE" }),
	deleteImage: (id: string, imageId: string) =>
		fetchVoid(`/admin/locations/${id}/images/${imageId}`, { method: "DELETE" }),
	duplicateReviews: {
		list: (status: DuplicateReviewStatus | "all" = "pending", limit = 50, offset = 0) =>
			fetchApi(
				`/admin/locations/duplicates?status=${status}&limit=${limit}&offset=${offset}`,
				paginatedSchema(duplicateReviewSchema),
			),
		update: (id: string, body: UpdateDuplicateReview) =>
			fetchApi(`/admin/locations/duplicates/${id}`, duplicateReviewSchema, {
				body: JSON.stringify(body),
				method: "PUT",
			}),
	},
	get: (id: string) => fetchApi(`/admin/locations/${id}`, locationSchema),
	imageReviews: {
		list: (status: ImageReviewStatus | "all" = "pending", limit = 50, offset = 0) =>
			fetchApi(
				`/admin/locations/image-reviews?status=${status}&limit=${limit}&offset=${offset}`,
				paginatedSchema(imageReviewSchema),
			),
		update: (id: string, body: UpdateImageReview) =>
			fetchApi(`/admin/locations/image-reviews/${id}`, imageReviewSchema, {
				body: JSON.stringify(body),
				method: "PATCH",
			}),
	},
	importImage: (
		id: string,
		body: { url: string; attribution?: string; is_primary?: boolean },
	) =>
		fetchApi(`/admin/locations/${id}/images`, locationImageSchema, {
			body: JSON.stringify(body),
			method: "POST",
		}),
	list: ({
		limit,
		offset,
		q,
		sort_by,
		order,
		source,
		verification,
		is_hidden,
		country,
		city,
		needs_images,
	}: LocationsListParams = {}) => {
		const qs = toSearchParams({
			city,
			country,
			is_hidden,
			limit,
			needs_images,
			offset,
			order,
			q,
			sort_by,
			source,
			verification,
		});
		return fetchApi(`/admin/locations${qs ? `?${qs}` : ""}`, paginatedSchema(locationSchema));
	},
	merge: (duplicateId: string, into: string) =>
		fetchApi(`/admin/locations/${duplicateId}/merge`, locationSchema, {
			body: JSON.stringify({ into }),
			method: "POST",
		}),
	replaceImage: (id: string, imageId: string, file: File) => {
		const formData = new FormData();
		formData.append(LOCATION_IMAGE_FIELD, file);
		return fetchFormData(
			`/admin/locations/${id}/images/${imageId}`,
			locationImageSchema,
			formData,
			"PUT",
		);
	},
	reports: {
		list: (status: ReportStatus | "all" = "open", limit = 50, offset = 0) =>
			fetchApi(
				`/admin/locations/reports?status=${status}&limit=${limit}&offset=${offset}`,
				paginatedSchema(locationReportSchema),
			),
		summary: (status: ReportStatus | "all" = "open", limit = 50, offset = 0) =>
			fetchApi(
				`/admin/locations/reports/summary?status=${status}&limit=${limit}&offset=${offset}`,
				paginatedSchema(locationReportSummarySchema),
			),
		updateStatus: (id: string, body: UpdateReportStatus) =>
			fetchApi(`/admin/locations/reports/${id}`, locationReportSchema, {
				body: JSON.stringify(body),
				method: "PATCH",
			}),
	},
	scrapeBulk: (limit?: number) =>
		fetchApi(`/admin/locations/scrapes`, scrapeBulkResponseSchema, {
			body: JSON.stringify({ limit: limit ?? 0 }),
			method: "POST",
		}),
	scrapeOne: (id: string) =>
		fetchApi(`/admin/locations/${id}/scrapes`, scrapeRunResultSchema, {
			method: "POST",
		}),
	screens: {
		list: (resolution: SatelliteScreenResolution | "all" = "pending", limit = 50, offset = 0) =>
			fetchApi(
				`/admin/locations/screens?resolution=${resolution}&limit=${limit}&offset=${offset}`,
				paginatedSchema(satelliteScreenSchema),
			),
		run: (limit?: number) =>
			fetchApi(`/admin/locations/screens`, screenRunResponseSchema, {
				body: JSON.stringify({ limit: limit ?? 0 }),
				method: "POST",
			}),
		update: (id: string, body: UpdateSatelliteScreen) =>
			fetchApi(`/admin/locations/screens/${id}`, satelliteScreenSchema, {
				body: JSON.stringify(body),
				method: "PATCH",
			}),
	},
	update: (id: string, data: unknown) =>
		fetchApi(`/admin/locations/${id}`, locationSchema, {
			body: JSON.stringify(data),
			method: "PATCH",
		}),
	updateImage: (id: string, imageId: string, data: UpdateLocationImage) =>
		fetchApi(`/admin/locations/${id}/images/${imageId}`, locationImageSchema, {
			body: JSON.stringify(data),
			method: "PATCH",
		}),
	uploadImage: (id: string, file: File, opts?: { isPrimary?: boolean; caption?: string }) => {
		const formData = new FormData();
		formData.append(LOCATION_IMAGE_FIELD, file);
		if (opts?.isPrimary) formData.append("is_primary", "true");
		if (opts?.caption) formData.append("caption", opts.caption);
		return fetchFormData(`/admin/locations/${id}/images`, locationImageSchema, formData);
	},
	verify: (id: string) =>
		fetchApi(`/admin/locations/${id}/verify`, locationSchema, {
			method: "POST",
		}),
};
